import { type FC, type ReactNode } from "react";
import { type Kmat, type LinearSubstep, type SubstepTile } from "~/features/configurator/Linear/models";
import { useDispatch, useSelector } from "react-redux";
import SubstepFrame from "~/features/configurator/Linear/components/SubstepFrame";
import LinearTile from "~/features/configurator/Linear/components/LinearTile";
import { defaultMemoize } from "reselect";
import { Col, Row } from "~/common/components/Layout";
import { updateCsticValuesFromTile } from "~/features/configurator/Linear/redux/actions";
import { getKmats } from "~/features/configurator/Linear/redux/selectors";
import { Card } from "~/features/configurator/Linear/components/Card";
import { isDebugLanguageEnabled } from "~/utils/language/isDebugLanguageEnabled";
import type { RootState } from "~/app/store";
import type { CustomDispatch } from "~/app/store/types";
import { makeRenderTile } from "~/features/configurator/common/components/GenericTileSubstep/GenericTileSubstep";
import { isTileEnabled, isTileStandard } from "~/features/configurator/common/tileUtils";
import { getMainKmat } from "~/features/configurator/Shaft/common/commonShaftHelpers";
import { useTranslation } from "react-i18next";

interface Props {
  csticIndex: number;
  csticAmount: number;
  def: LinearSubstep;
}

const LinearLocatingFaceSubstep: FC<Props> = ({ def, ...props }) => {
  const debugLanguage = isDebugLanguageEnabled();
  const renderTitle = makeRenderTile(debugLanguage, def);

  return (
    <SubstepFrame
      title={def.name}
      description={def.description}
      info={def.info}
      isPicked={true}
      showError={false}
      errorMessage={""}
      {...props}
    >
      <div className={"w-100 mb-30"}>
        <Row className={"d-flex"}>
          {def.groupedTiles.map(({ tiles }, index) => (
            <Col size={4} key={index}>
              <SingleGuidewayTile tiles={tiles} renderTitle={renderTitle} def={def} index={index} />
            </Col>
          ))}
        </Row>
      </div>
    </SubstepFrame>
  );
};
export default LinearLocatingFaceSubstep;

interface TileProps {
  index: number;
  tiles: SubstepTile[];
  def: LinearSubstep;
  renderTitle: (data: SubstepTile) => ReactNode;
}

const SingleGuidewayTile = (props: TileProps) => {
  const { def, renderTitle } = props;
  const { t } = useTranslation();

  const kmats = useSelector(getKmats);
  const dispatch = useDispatch();
  const tiles = getTileData(def, dispatch, props.tiles, renderTitle, kmats);

  const isTwoTileLayout = tiles.length === 2;
  const cAbove = isTwoTileLayout ? tiles[1].isPicked : tiles[2].isPicked;
  const rAbove = isTwoTileLayout ? tiles[0].isPicked : tiles[0].isPicked;

  return (
    <Card disableHover={true}>
      <div className={"p-30"}>
        <p className={"f-bold mt-0 mb-3"}>{t("locating.face.guideway.set.label", { index: props.index + 1 })}</p>

        <Row>
          <Col size={6}>
            <LinearTile {...tiles[0]} />
          </Col>
          <Col size={6}>
            <LinearTile {...(isTwoTileLayout ? tiles[1] : tiles[2])} />
          </Col>
        </Row>

        <div className={"my-3"}>
          <BearingImage cAbove={cAbove} rAbove={rAbove} />
        </div>

        {!isTwoTileLayout && (
          <Row>
            <Col size={6}>
              <LinearTile {...tiles[1]} />
            </Col>
            <Col size={6}>
              <LinearTile {...tiles[3]} />
            </Col>
          </Row>
        )}
      </div>
    </Card>
  );
};

const getTileData = defaultMemoize(
  (
    stepElement: LinearSubstep,
    dispatch: CustomDispatch<RootState>,
    tiles: SubstepTile[],
    renderTitle: (data: SubstepTile) => ReactNode,
    kmats: Dict<Kmat[]>,
  ) => {
    const allTiles = tiles.map((tile, index) => ({
      index,
      onClickHandler: () => dispatch(updateCsticValuesFromTile(stepElement.type, index, tile)),
      imageUrl: tile.imageUrl,
      title: renderTitle(tile),
      description: tile.description,
      info: tile.info,
      isPicked: isTileSelected(tile, kmats),
      isDisabled: !isTileEnabled(tile, kmats),
      isStandard: isTileStandard(tile, kmats),
    }));

    return allTiles;
  },
);

interface BearingImageProps {
  rAbove: boolean;
  cAbove: boolean;
}

const BearingImage: FC<BearingImageProps> = ({ rAbove, cAbove }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      viewBox="0 0 656 369"
      className={"w-100"}
    >
      <defs>
        <rect id="guideway_set_railabove_carriageabove@1x-e" width="656" height="369" x="0" y="0" />
        <rect id="guideway_set_railabove_carriageabove@1x-a" width="1.691" height="104.825" x="3.381" y="30.433" />
        <mask
          id="guideway_set_railabove_carriageabove@1x-g"
          width="1.691"
          height="104.825"
          x="0"
          y="0"
          fill="#fff"
          maskContentUnits="userSpaceOnUse"
          maskUnits="objectBoundingBox"
        >
          <use xlinkHref="#guideway_set_railabove_carriageabove@1x-a" />
        </mask>
        <rect id="guideway_set_railabove_carriageabove@1x-b" width="1.691" height="135.258" x="33.814" y="0" />
        <mask
          id="guideway_set_railabove_carriageabove@1x-h"
          width="1.691"
          height="135.258"
          x="0"
          y="0"
          fill="#fff"
          maskContentUnits="userSpaceOnUse"
          maskUnits="objectBoundingBox"
        >
          <use xlinkHref="#guideway_set_railabove_carriageabove@1x-b" />
        </mask>
        <rect id="guideway_set_railabove_carriageabove@1x-c" width="1.691" height="135.258" x="3.381" y="0" />
        <mask
          id="guideway_set_railabove_carriageabove@1x-i"
          width="1.691"
          height="135.258"
          x="0"
          y="0"
          fill="#fff"
          maskContentUnits="userSpaceOnUse"
          maskUnits="objectBoundingBox"
        >
          <use xlinkHref="#guideway_set_railabove_carriageabove@1x-c" />
        </mask>
        <rect id="guideway_set_railabove_carriageabove@1x-d" width="1.691" height="104.825" x="52.412" y="30.433" />
        <mask
          id="guideway_set_railabove_carriageabove@1x-j"
          width="1.691"
          height="104.825"
          x="0"
          y="0"
          fill="#fff"
          maskContentUnits="userSpaceOnUse"
          maskUnits="objectBoundingBox"
        >
          <use xlinkHref="#guideway_set_railabove_carriageabove@1x-d" />
        </mask>
      </defs>
      <g fill="none" fillRule="evenodd" transform="translate(-548 -648)">
        <rect width="2302" height="2643" fill="#FFF" />
        <g transform="translate(548 648)">
          <mask id="guideway_set_railabove_carriageabove@1x-f" fill="#fff">
            <use xlinkHref="#guideway_set_railabove_carriageabove@1x-e" />
          </mask>
          <g mask="url(#guideway_set_railabove_carriageabove@1x-f)">
            <g transform="translate(37.196 116.66)">
              <g transform="translate(544.412)">
                <path
                  fill="#646464"
                  fillRule="nonzero"
                  d="M10.0848905,158.948293 C10.8432936,158.948293 11.5534022,158.823084 12.2152163,158.572668 C12.8770303,158.322252 13.5656747,157.892967 14.2811493,157.284814 C14.3383873,157.82142 14.4671727,158.290056 14.6675056,158.690722 L14.6675056,158.690722 L16.6851442,158.690722 C16.4418828,158.254282 16.2791123,157.798167 16.1968327,157.322376 C16.1145531,156.846586 16.0734133,155.71077 16.0734133,153.914928 L16.0734133,153.914928 L16.0724474,151.08959 C16.0666521,150.364528 16.0347777,149.853894 15.9768243,149.557688 C15.8623483,149.028236 15.6620154,148.590008 15.3758255,148.243003 C15.0896357,147.895998 14.642464,147.608019 14.0343106,147.379067 C13.4261571,147.150115 12.6355576,147.035639 11.6625121,147.035639 C10.6823118,147.035639 9.82016484,147.169791 9.0760712,147.438094 C8.33197756,147.706397 7.7631752,148.089176 7.36966414,148.586431 C6.97615308,149.083686 6.69354059,149.736556 6.52182668,150.545043 L6.52182668,150.545043 L8.41067977,150.802614 C8.61816742,149.994127 8.93834233,149.430691 9.37120449,149.112305 C9.80406666,148.793918 10.4748242,148.634725 11.383477,148.634725 C12.3565225,148.634725 13.089884,148.852945 13.5835615,149.289385 C13.9484536,149.611348 14.1308996,150.165841 14.1308996,150.952863 C14.1308996,151.024411 14.1273223,151.192547 14.1201675,151.457273 C13.3832286,151.714844 12.2348918,151.936641 10.6751571,152.122664 C9.90959917,152.215676 9.33721945,152.312265 8.95801788,152.412432 C8.44287613,152.555526 7.97960629,152.766591 7.56820836,153.045627 C7.15681043,153.324662 6.82411472,153.69492 6.57012121,154.156401 C6.31612771,154.617882 6.18913096,155.127658 6.18913096,155.685728 C6.18913096,156.637309 6.52540405,157.418965 7.19795023,158.030696 C7.8704964,158.642427 8.83280982,158.948293 10.0848905,158.948293 Z M10.5463716,157.435064 C9.79512323,157.435064 9.22453219,157.26335 8.8345985,156.919922 C8.44466481,156.576494 8.24969797,156.147209 8.24969797,155.632067 C8.24969797,155.295794 8.3391323,154.989929 8.51800097,154.714471 C8.69686963,154.439014 8.95086313,154.229737 9.27998148,154.086642 C9.60909982,153.943547 10.1707474,153.814762 10.9649243,153.700286 C12.3672546,153.499953 13.4190024,153.256692 14.1201675,152.970502 L14.1201675,152.970502 L14.1201675,153.678822 C14.1201675,154.530237 14.0164237,155.174164 13.8089361,155.610603 C13.5370557,156.175828 13.1113483,156.621211 12.5318138,156.946752 C11.9522793,157.272293 11.2904653,157.435064 10.5463716,157.435064 Z M21.4287411,168.690722 L21.4287411,161.704112 L23.8434681,161.704112 C24.3800741,161.704112 24.7664304,161.729153 25.002537,161.779236 C25.3245006,161.857939 25.6410982,161.997456 25.9523297,162.197789 C26.2635611,162.398122 26.6159324,162.748705 27.0094435,163.249537 C27.4029545,163.750369 27.9037868,164.472999 28.5119402,165.417425 L28.5119402,165.417425 L30.5939715,168.690722 L33.2126087,168.690722 L30.4759182,164.408606 C29.9321574,163.5715 29.3562003,162.873913 28.7480469,162.315842 C28.461857,162.058272 28.0433043,161.797123 27.4923889,161.532398 C29.0020404,161.32491 30.1181808,160.838387 30.8408102,160.072829 C31.5634396,159.307272 31.9247543,158.366422 31.9247543,157.250282 C31.9247543,156.384558 31.7065346,155.593958 31.270095,154.878483 C30.8336555,154.163009 30.2505437,153.663965 29.5207595,153.381353 C28.7909754,153.09874 27.7249181,152.957434 26.3225878,152.957434 L26.3225878,152.957434 L19.3467099,152.957434 L19.3467099,168.690722 L21.4287411,168.690722 Z M25.9040351,159.901115 L21.4287411,159.901115 L21.4287411,154.696037 L26.4084447,154.696037 C27.5746684,154.696037 28.427872,154.935721 28.9680553,155.415089 C29.5082387,155.894457 29.7783304,156.506188 29.7783304,157.250282 C29.7783304,157.758269 29.6388128,158.228693 29.3597777,158.661556 C29.0807426,159.094418 28.672922,159.409227 28.136316,159.605982 C27.5997101,159.802738 26.8556164,159.901115 25.9040351,159.901115 L25.9040351,159.901115 Z"
                />
                <use
                  stroke="#CED5DA"
                  strokeDasharray="1.691"
                  strokeWidth="3.381"
                  mask="url(#guideway_set_railabove_carriageabove@1x-g)"
                  xlinkHref="#guideway_set_railabove_carriageabove@1x-a"
                />
                <use
                  stroke="#CED5DA"
                  strokeDasharray="1.691"
                  strokeWidth="3.381"
                  mask="url(#guideway_set_railabove_carriageabove@1x-h)"
                  xlinkHref="#guideway_set_railabove_carriageabove@1x-b"
                />
                <polygon
                  fill="#CED5DA"
                  points="15.555 16.353 9.284 10.144 15.555 3.936 13.624 2.029 5.41 10.144 13.624 18.26"
                  transform="translate(0 116.66)"
                />
                <rect width="21.979" height="3.381" x="8.454" y="125.113" fill="#CED5DA" />
                <polygon
                  fill="#CED5DA"
                  points="5.41 16.353 11.681 10.144 5.41 3.936 7.341 2.029 15.555 10.144 7.341 18.26"
                  transform="translate(18.598 116.66)"
                />
              </g>
              <use
                stroke="#CED5DA"
                strokeDasharray="1.691"
                strokeWidth="3.381"
                mask="url(#guideway_set_railabove_carriageabove@1x-i)"
                xlinkHref="#guideway_set_railabove_carriageabove@1x-c"
              />
              <use
                stroke="#CED5DA"
                strokeDasharray="1.691"
                strokeWidth="3.381"
                mask="url(#guideway_set_railabove_carriageabove@1x-j)"
                xlinkHref="#guideway_set_railabove_carriageabove@1x-d"
              />
              <polygon
                fill="#CED5DA"
                points="15.555 16.353 9.284 10.144 15.555 3.936 13.624 2.029 5.41 10.144 13.624 18.26"
                transform="translate(0 116.66)"
              />
              <rect width="40.577" height="3.381" x="8.454" y="125.113" fill="#CED5DA" />
              <polygon
                fill="#CED5DA"
                points="5.41 16.353 11.681 10.144 5.41 3.936 7.341 2.029 15.555 10.144 7.341 18.26"
                transform="translate(37.196 116.66)"
              />
              <path
                fill="#646464"
                fillRule="nonzero"
                d="M21.2083199,158.948293 C21.966723,158.948293 22.6768316,158.823084 23.3386457,158.572668 C24.0004598,158.322252 24.6891041,157.892967 25.4045788,157.284814 C25.4618167,157.82142 25.5906022,158.290056 25.7909351,158.690722 L25.7909351,158.690722 L27.8085736,158.690722 C27.5653122,158.254282 27.4025417,157.798167 27.3202622,157.322376 C27.2379826,156.846586 27.1968428,155.71077 27.1968428,153.914928 L27.1968428,151.33922 C27.1968428,150.48065 27.1646464,149.886806 27.1002537,149.557688 C26.9857778,149.028236 26.7854449,148.590008 26.499255,148.243003 C26.2130651,147.895998 25.7658935,147.608019 25.15774,147.379067 C24.5495866,147.150115 23.7589871,147.035639 22.7859415,147.035639 C21.8057412,147.035639 20.9435943,147.169791 20.1995006,147.438094 C19.455407,147.706397 18.8866046,148.089176 18.4930936,148.586431 C18.0995825,149.083686 17.81697,149.736556 17.6452561,150.545043 L17.6452561,150.545043 L19.5341092,150.802614 C19.7415969,149.994127 20.0617718,149.430691 20.4946339,149.112305 C20.9274961,148.793918 21.5982536,148.634725 22.5069064,148.634725 C23.4799519,148.634725 24.2133135,148.852945 24.706991,149.289385 C25.0718831,149.611348 25.2543291,150.165841 25.2543291,150.952863 C25.2543291,151.024411 25.2507517,151.192547 25.243597,151.457273 C24.5066581,151.714844 23.3583213,151.936641 21.7985865,152.122664 C21.0330286,152.215676 20.4606489,152.312265 20.0814473,152.412432 C19.5663056,152.555526 19.1030357,152.766591 18.6916378,153.045627 C18.2802399,153.324662 17.9475442,153.69492 17.6935507,154.156401 C17.4395572,154.617882 17.3125604,155.127658 17.3125604,155.685728 C17.3125604,156.637309 17.6488335,157.418965 18.3213797,158.030696 C18.9939258,158.642427 19.9562393,158.948293 21.2083199,158.948293 Z M21.6698011,157.435064 C20.9185527,157.435064 20.3479616,157.26335 19.9580279,156.919922 C19.5680943,156.576494 19.3731274,156.147209 19.3731274,155.632067 C19.3731274,155.295794 19.4625617,154.989929 19.6414304,154.714471 C19.8202991,154.439014 20.0742926,154.229737 20.4034109,154.086642 C20.7325293,153.943547 21.2941769,153.814762 22.0883537,153.700286 C23.4906841,153.499953 24.5424318,153.256692 25.243597,152.970502 L25.243597,152.970502 L25.243597,153.678822 C25.243597,154.530237 25.1398531,155.174164 24.9323655,155.610603 C24.6604851,156.175828 24.2347777,156.621211 23.6552432,156.946752 C23.0757088,157.272293 22.4138947,157.435064 21.6698011,157.435064 Z M40.1827078,168.690722 L40.1827078,166.834065 L32.4341173,166.834065 L32.4341173,152.957434 L30.352086,152.957434 L30.352086,168.690722 L40.1827078,168.690722 Z"
              />
            </g>
          </g>
          <g mask="url(#guideway_set_railabove_carriageabove@1x-f)">
            <g transform="translate(40.577 69.32)">
              <g transform="translate(0 18.598)">
                <rect width="140.33" height="118.351" x="319.546" fill="#CED5DA" />
                <rect width="20.289" height="81.155" x="299.258" y="18.598" fill="#AAB1B4" />
                <path
                  fill="#CED5DA"
                  d="M299.257732,28.742268 L299.257732,89.6082474 L0,89.6082474 L0,28.742268 L299.257732,28.742268 Z M50.7216495,49.0309278 C45.1190908,49.0309278 40.5773196,53.572699 40.5773196,59.1752577 C40.5773196,64.7778164 45.1190908,69.3195876 50.7216495,69.3195876 C56.3242082,69.3195876 60.8659794,64.7778164 60.8659794,59.1752577 C60.8659794,53.572699 56.3242082,49.0309278 50.7216495,49.0309278 Z M248.536082,49.0309278 C242.933524,49.0309278 238.391753,53.572699 238.391753,59.1752577 C238.391753,64.7778164 242.933524,69.3195876 248.536082,69.3195876 C254.138641,69.3195876 258.680412,64.7778164 258.680412,59.1752577 C258.680412,53.572699 254.138641,49.0309278 248.536082,49.0309278 Z M148.783505,49.0309278 C143.180946,49.0309278 138.639175,53.572699 138.639175,59.1752577 C138.639175,64.7778164 143.180946,69.3195876 148.783505,69.3195876 C154.386064,69.3195876 158.927835,64.7778164 158.927835,59.1752577 C158.927835,53.572699 154.386064,49.0309278 148.783505,49.0309278 Z"
                />
                <rect width="20.289" height="81.155" x="459.876" y="18.598" fill="#AAB1B4" />
                <path
                  fill="#D4DADF"
                  d="M506.063905,28.7417706 C509.357392,33.5926008 511,38.9810945 511,44.9320672 C511,48.5131655 509.831465,51.5930888 506.957481,56.7231504 L506.957481,56.7231504 L505.734424,58.8917887 C502.259075,65.1032891 501,68.4123928 501,72.9320672 C501,79.3503886 502.58869,84.9185472 505.767018,89.6093154 L480.164948,89.6089002 L480.164948,28.7429208 Z M576.536082,28.7429208 L576.536082,89.6089002 L508.229739,89.6097176 C504.743654,85.0342206 503,79.4866805 503,72.9320672 C503,69.0157738 504.079878,66.0259829 507.10614,60.5407785 L507.10614,60.5407785 L509.453192,56.3346786 C511.943524,51.7053833 513,48.6017139 513,44.9320672 C513,39.035133 511.480502,33.631812 508.448131,28.741976 L576.536082,28.7429208 Z M546.103093,49.0309278 C540.500534,49.0309278 535.958763,53.572699 535.958763,59.1752577 C535.958763,64.7778164 540.500534,69.3195876 546.103093,69.3195876 C551.705651,69.3195876 556.247423,64.7778164 556.247423,59.1752577 C556.247423,53.572699 551.705651,49.0309278 546.103093,49.0309278 Z"
                />
              </g>
              {!rAbove && <rect width="76.082" height="5.072" x="500.454" y="121.732" fill="#00893D" />}
              {!cAbove && <rect width="140.33" height="5.072" x="319.546" y="150.474" fill="#00893D" />}
              {!rAbove && <rect width="278.969" height="5.072" y="121.732" fill="#00893D" />}
              {rAbove && <rect width="76.082" height="5.072" x="500.454" y="28.742" fill="#00893D" />}
              {cAbove && <rect width="140.33" height="5.072" x="319.546" fill="#00893D" />}
              {rAbove && <rect width="278.969" height="5.072" y="28.742" fill="#00893D" />}
            </g>
          </g>
        </g>
      </g>
    </svg>
  );
};

function isTileSelected(tile: SubstepTile, kmats: Dict<Kmat[]>) {
  const mainKmatCstics = getMainKmat({ kmats }).cstics;
  return Object.entries(tile.cstics).every(([key, value]) => (mainKmatCstics[key].value ?? "") === (value ?? ""));
}
